/**
 * What a chip should put in its `<img src>`, given everything that can go wrong
 * between "this send has a picture" and "the picture is on screen".
 *
 * Three things have to agree before a thumbnail paints:
 *
 *  1. The send actually has an asset (`mediaUrl`).
 *  2. We have a URL this document is allowed to load. On the website that IS
 *     `mediaUrl`; inside the extension the page's CSP refuses our asset host, so
 *     it is the `data:` URL the background worker handed back. Either way it
 *     arrives here as `resolvedUrl` — the caller does not need to know which
 *     context it is in, only whether it has something loadable yet.
 *  3. The browser did not then reject it anyway (`imageFailed`, from the <img>
 *     onError the chip already had for 404s and deleted assets).
 *
 * If any of those is missing the answer is `undefined`, which the chip reads as
 * "no picture" and renders its text treatment. That single return path is why a
 * worker failure, a still-in-flight fetch, an expired asset and a send with no
 * media all degrade identically — to text, never to a blank frame.
 */
export interface ChipThumbnailInput {
    /** The asset URL on the event, if any. */
    mediaUrl?: string;
    /** A URL loadable in THIS context: the same URL on web, a data: URL in the extension. */
    resolvedUrl?: string;
    /** True while the resolver is still working — treated as "no picture yet". */
    isLoading?: boolean;
    /** The <img> reported an error for whatever we last gave it. */
    imageFailed?: boolean;
}

export function chipThumbnailSrc(input: ChipThumbnailInput): string | undefined {
    const { mediaUrl, resolvedUrl, isLoading, imageFailed } = input;
    if (!mediaUrl) return undefined;
    if (imageFailed) return undefined;
    if (isLoading) return undefined;
    if (!resolvedUrl) return undefined;
    return resolvedUrl;
}
